// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the list grammar
//  ---------------------------------------------------------------------------
//  Bullets, numbers and to-dos, and what Tab, Shift-Tab and Enter do to them.
//  Pure string work: a source, a caret, and the source and caret it becomes.
//  md-markdown.jsx binds the keys and draws the result; nothing about the DOM,
//  the selection or the render lives here, which is the whole point — these are
//  the operations that rewrite the writer's text underneath their caret, and a
//  contentEditable is the one place in this app that cannot be unit-tested.
//
//  NESTING IS TEXT, not a flag. A sub-item is an item with two more spaces in
//  front of it, exactly what a writer would type by hand, so a record that is
//  indented in this editor is still the markdown it looks like. Two spaces is
//  what CommonMark itself produces for a bullet's children, and it is what Tab
//  already inserted here before Tab meant anything else.
//
//  THE NUMBERS IN THE SOURCE ARE NOT THE NUMBERS ON SCREEN. md-markdown draws
//  ordered items from CSS counters, so the visible sequence is always right no
//  matter what the digits say (see the mdRenderInner note there). We still keep
//  the source honest where it is cheap to — a continued item counts up, an item
//  that climbs a level is numbered for the level it lands in — but nothing here
//  rewrites a line the writer is not standing on to make the digits agree.
//
//  The three rules that took the most care, each pinned in tests/list.test.js:
//    • an item carries its children. Indent a parent, leave the children, and
//      they are silently no longer its children.
//    • the first item of a list cannot indent. There is nothing above it to be
//      a child of, and every editor a writer has already met refuses this.
//    • Enter on an empty NESTED item climbs one level instead of leaving the
//      list. That is "press Enter twice to get back to the parent"; leaving the
//      list altogether is what the LAST of those Enters does, at the top.
// ════════════════════════════════════════════════════════════════════════════

/** A list line, taken apart. `level` is the nesting depth, `pad` is what made it. */
interface MdListItem {
  pad: string;
  marker: string;
  kind: 'li' | 'ol' | 'todo';
  level: number;
  content: string;
}

/** A rewritten source and where the caret goes in it. */
interface MdListEdit {
  src: string;
  caret: number;
}

(function (g: any) {
  'use strict';

  /* One level of nesting, as characters. */
  const MD_LIST_INDENT = '  ';

  /* How deep nesting is allowed to go. Not a technical limit — the writing
     column is finite, and past this the text has more margin than measure. */
  const MD_LIST_MAX = 5;

  /* The to-do form is matched BEFORE the plain bullet on purpose: "- [ ] " also
     starts with "- ", and the looser pattern would win and eat the checkbox. */
  const MD_ITEM_RE = /^([ \t]*)(- \[[ xX]\] |[-*] |\d+\. )(.*)$/;

  const mdListLevel = (pad: string): number =>
    Math.min(MD_LIST_MAX, Math.floor(pad.length / MD_LIST_INDENT.length));

  /** What kind of list item this line is — or null if it is not one at all. */
  function mdListParse(line: unknown): MdListItem | null {
    if (typeof line !== 'string') return null;
    const m = MD_ITEM_RE.exec(line);
    if (!m) return null;
    const pad = m[1] || '';
    const marker = m[2] || '';
    const kind: MdListItem['kind'] =
      /^\d/.test(marker) ? 'ol' : marker.charAt(2) === '[' ? 'todo' : 'li';
    return { pad, marker, kind, level: mdListLevel(pad), content: m[3] || '' };
  }

  /** Turn the compact author-facing `[]` line into the canonical task marker.
      The source keeps `- [ ] ` so every existing list operation and exported
      Markdown remains compatible; this only makes starting one feel lighter. */
  function mdTodoShortcut(src: unknown, off: unknown, firstLineTitle?: boolean): MdListEdit | null {
    if (typeof src !== 'string' || typeof off !== 'number') return null;
    const caret = Math.max(0, Math.min(off, src.length));
    const ls = src.lastIndexOf('\n', caret - 1) + 1;
    if (firstLineTitle && ls === 0) return null;
    let le = src.indexOf('\n', ls);
    if (le < 0) le = src.length;
    if (caret !== le) return null;
    const m = /^([ \t]*)\[\]$/.exec(src.slice(ls, le));
    if (!m) return null;
    const line = (m[1] || '') + '- [ ] ';
    return { src: src.slice(0, ls) + line + src.slice(le), caret: ls + line.length };
  }

  /** Which line of `src` the offset falls on. A position on a seam belongs to
      the line that ENDS there — that is where the caret visibly sits. */
  function mdLineAt(lines: string[], off: number): number {
    let at = 0;
    for (let i = 0; i < lines.length; i++) {
      at += (lines[i] as string).length;
      if (off <= at) return i;
      at += 1; // the newline
    }
    return lines.length - 1;
  }

  /* Every line that moves when line `i` moves: the item itself and everything
     nested under it, stopping at the first line that is not deeper. */
  function mdSubtree(items: (MdListItem | null)[], i: number, into: Set<number>): void {
    into.add(i);
    const base = (items[i] as MdListItem).level;
    for (let j = i + 1; j < items.length; j++) {
      const it = items[j];
      if (!it || it.level <= base) return;
      into.add(j);
    }
  }

  /* Tab and Shift-Tab are the same operation with the sign flipped, so they are
     one function: pick the list lines the selection touches, add their children,
     check every one of them can legally move, then rewrite the pads.

     Returns null when the selection holds no list line at all — that is Tab
     meaning something else entirely, and it is the caller's to decide. When a
     move is merely REFUSED (the first item of a list, the depth cap) the source
     comes back unchanged, so a key that had a meaning here does nothing rather
     than doing something else. */
  function mdListShift(src: unknown, start: unknown, end: unknown, dir: number): MdListEdit | null {
    if (typeof src !== 'string' || typeof start !== 'number') return null;
    const s = Math.max(0, Math.min(start, src.length));
    const e = Math.max(s, Math.min(typeof end === 'number' ? end : s, src.length));
    const lines = src.split('\n');
    const items = lines.map(mdListParse);
    const first = mdLineAt(lines, s);
    const last = mdLineAt(lines, e);

    const moving = new Set<number>();
    let any = false;
    for (let i = first; i <= last; i++) {
      if (!items[i]) continue;
      any = true;
      /* Indenting needs a parent: the line above has to be a list item at least
         as deep as this one. Not one, so a list cannot skip a level; not none,
         so the first item of a list stays where it is. A blank line above is a
         list that already ended, and mdListParse says so by returning null. */
      if (dir > 0) {
        const above = i > 0 ? items[i - 1] : null;
        if (!above || above.level < (items[i] as MdListItem).level) continue;
      } else if ((items[i] as MdListItem).level === 0) continue;
      mdSubtree(items, i, moving);
    }
    if (!any) return null;
    if (!moving.size) return { src, caret: e };
    // The cap is checked over the WHOLE subtree: refusing only the deepest child
    // would slide it up into its own parent.
    if (dir > 0) {
      for (const i of moving) if ((items[i] as MdListItem).level + 1 > MD_LIST_MAX) return { src, caret: e };
    }

    const caretLine = mdLineAt(lines, e);
    let caret = e;
    const out = lines.map((ln, i) => {
      if (!moving.has(i)) return ln;
      const it = items[i] as MdListItem;
      /* The pad is REBUILT from the level rather than nudged by two characters,
         so a hand-typed odd indent is straightened the first time the line moves
         instead of staying odd forever. */
      const next = MD_LIST_INDENT.repeat(Math.max(0, Math.min(MD_LIST_MAX, it.level + dir)));
      const delta = next.length - it.pad.length;
      if (i <= caretLine) caret += delta;
      return next + it.marker + it.content;
    });
    // Never let the caret fall off the front of its own line.
    const lineStart = out.slice(0, caretLine).reduce((n, l) => n + l.length + 1, 0);
    if (caret < lineStart) caret = lineStart;
    return { src: out.join('\n'), caret };
  }

  /** Tab — nest the list lines the caret or selection touches one level deeper. */
  function mdListIndent(src: unknown, start: unknown, end?: unknown): MdListEdit | null {
    return mdListShift(src, start, end, 1);
  }

  /** Shift-Tab — lift them back out one level. */
  function mdListOutdent(src: unknown, start: unknown, end?: unknown): MdListEdit | null {
    return mdListShift(src, start, end, -1);
  }

  /* What number an ordered item gets when it arrives at `level` above offset
     `ls`. Deeper lines in between are skipped — they are this item's children,
     not its siblings — and anything shallower, or not a list at all, ends the
     sequence, so the item starts a new list at 1. Mirrors the counter-reset
     rule md-markdown draws with; the two have to agree or the source and the
     screen disagree about where a list began. */
  function mdListNextNum(src: string, ls: number, level: number): number {
    if (ls <= 0) return 1;
    const above = src.slice(0, ls - 1).split('\n');
    for (let i = above.length - 1; i >= 0; i--) {
      const it = mdListParse(above[i]);
      if (!it || it.level < level) return 1;
      if (it.level > level) continue;
      return it.kind === 'ol' ? (parseInt(it.marker, 10) || 0) + 1 : 1;
    }
    return 1;
  }

  /** Enter, inside a list. Null means "this is not a list line" — an ordinary
      line break, which is the caller's job and not this file's. */
  function mdListContinue(src: unknown, off: unknown, firstLineTitle?: boolean): MdListEdit | null {
    if (typeof src !== 'string' || typeof off !== 'number') return null;
    const ls = src.lastIndexOf('\n', off - 1) + 1;
    let le = src.indexOf('\n', ls);
    if (le < 0) le = src.length;
    // In title mode line 0 is the record's NAME. It is not a list and never
    // becomes one; Enter there is only ever a line break.
    if (firstLineTitle && ls === 0) return null;
    const item = mdListParse(src.slice(ls, le));
    if (!item) return null;
    const contentStart = ls + item.pad.length + item.marker.length;
    // Caret at or before the marker: Enter there means "open a line above me",
    // which a plain newline already does. Leave it alone.
    if (off < contentStart) return null;

    if (!src.slice(contentStart, le).trim()) {
      /* An empty item is the writer saying "no more of these". One level at a
         time: climb out to the parent while there is one, and only leave the
         list when the item was already at the top. */
      if (item.level > 0) {
        const level = item.level - 1;
        const marker =
          item.kind === 'ol' ? mdListNextNum(src, ls, level) + '. ' :
          item.kind === 'todo' ? '- [ ] ' :
          item.marker;
        const line = MD_LIST_INDENT.repeat(level) + marker;
        return { src: src.slice(0, ls) + line + src.slice(le), caret: ls + line.length };
      }
      // top level → drop the marker, leave an empty line, caret where the text was
      return { src: src.slice(0, ls) + src.slice(le), caret: ls };
    }

    /* Anywhere else in the item the line splits and BOTH halves keep a marker,
       which covers "Enter at the end" (the tail is empty) and "Enter mid-word"
       with one rule instead of two. A to-do always continues UNCHECKED, however
       the item above was left: done is a fact about one task, never something
       the next one inherits. */
    let next = item.marker;
    if (item.kind === 'todo') next = '- [ ] ';
    else if (item.kind === 'ol') next = (parseInt(item.marker, 10) + 1) + '. ';
    const ins = '\n' + item.pad + next;
    return { src: src.slice(0, off) + ins + src.slice(off), caret: off + ins.length };
  }

  g.MD_LIST_INDENT = MD_LIST_INDENT;
  g.MD_LIST_MAX = MD_LIST_MAX;
  g.mdListParse = mdListParse;
  g.mdTodoShortcut = mdTodoShortcut;
  g.mdListIndent = mdListIndent;
  g.mdListOutdent = mdListOutdent;
  g.mdListContinue = mdListContinue;

  // CommonJS escape hatch, following md-record.ts and md-link.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_LIST_INDENT, MD_LIST_MAX, mdListParse, mdTodoShortcut,
      mdListIndent, mdListOutdent, mdListContinue };
  }
})(typeof window !== 'undefined' ? window : globalThis);
